import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Loader2, TrendingUp, Briefcase, Bell, BarChart2,
  ArrowUpRight, BookOpen, Calendar, Globe, Star, X,
} from 'lucide-react';
import { globalSearch, AppResult, MarketResult, WebResult } from '../services/searchService';

const CATEGORY_ICON: Record<AppResult['category'], React.ReactNode> = {
  watchlist:   <Star size={13} className="text-yellow-400" />,
  portfolio:   <Briefcase size={13} className="text-green-400" />,
  alert:       <Bell size={13} className="text-red-400" />,
  analysis:    <BarChart2 size={13} className="text-blue-400" />,
  trade:       <TrendingUp size={13} className="text-purple-400" />,
  banker_lead: <ArrowUpRight size={13} className="text-amber-400" />,
  knowledge:   <BookOpen size={13} className="text-cyan-400" />,
  ipo:         <Calendar size={13} className="text-pink-400" />,
};

const CATEGORY_LABEL: Record<AppResult['category'], string> = {
  watchlist:   'Watchlist',
  portfolio:   'Portfolio',
  alert:       'Alert',
  analysis:    'Analysis',
  trade:       'Trade',
  banker_lead: 'Banker Lead',
  knowledge:   'Knowledge',
  ipo:         'IPO',
};

interface Results {
  app_results: AppResult[];
  market_results: MarketResult[];
  web_results: WebResult[];
}

export default function GlobalSearch() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Results | null>(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navigate = useNavigate();

  const totalItems = (results?.app_results.length ?? 0)
    + (results?.market_results.length ?? 0)
    + (results?.web_results.length ?? 0);

  // Debounced search
  useEffect(() => {
    if (!query.trim()) {
      setResults(null);
      setOpen(false);
      setActiveIndex(-1);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const data = await globalSearch(query.trim());
        setResults(data);
        setOpen(true);
        setActiveIndex(-1);
      } catch {
        // silently ignore search errors
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query]);

  // Click-outside to close
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Build flat navigable list for keyboard nav
  const flatItems: Array<{ type: 'app' | 'market' | 'web'; item: AppResult | MarketResult | WebResult }> = [
    ...(results?.app_results ?? []).map(item => ({ type: 'app' as const, item })),
    ...(results?.market_results ?? []).map(item => ({ type: 'market' as const, item })),
    ...(results?.web_results ?? []).map(item => ({ type: 'web' as const, item })),
  ];

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(i => Math.min(i + 1, flatItems.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      activateItem(flatItems[activeIndex]);
    } else if (e.key === 'Escape') {
      setOpen(false);
      inputRef.current?.blur();
    }
  };

  const activateItem = (entry: typeof flatItems[number]) => {
    setOpen(false);
    setQuery('');
    if (entry.type === 'app') {
      navigate((entry.item as AppResult).url);
    } else if (entry.type === 'market') {
      const m = entry.item as MarketResult;
      const symbol = m.exchange ? `${m.code}.${m.exchange}` : m.code;
      navigate(`/asset/${symbol}`);
    } else {
      window.open((entry.item as WebResult).url, '_blank', 'noopener,noreferrer');
    }
  };

  const hasResults = totalItems > 0;

  return (
    <div ref={containerRef} className="relative">
      <Search
        size={18}
        className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none z-10"
      />

      {loading && (
        <Loader2
          size={14}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 animate-spin z-10"
        />
      )}

      {query && !loading && (
        <button
          onClick={() => { setQuery(''); setResults(null); setOpen(false); }}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 z-10"
        >
          <X size={14} />
        </button>
      )}

      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={e => setQuery(e.target.value)}
        onFocus={() => { if (results && totalItems > 0) setOpen(true); }}
        onKeyDown={handleKeyDown}
        placeholder="Search assets, markets..."
        className="bg-slate-700/50 border border-slate-600 rounded-lg pl-10 pr-8 py-2 w-80 text-sm focus:outline-none transition-colors"
        style={{ '--tw-ring-color': 'var(--accent-400)' } as React.CSSProperties}
        onFocusCapture={e => { e.currentTarget.style.borderColor = 'var(--accent-400)'; }}
        onBlurCapture={e => { e.currentTarget.style.borderColor = ''; }}
      />

      {open && (
        <div className="absolute top-full mt-2 left-0 w-[480px] bg-slate-800 border border-slate-700 rounded-xl shadow-2xl z-[200] overflow-hidden">
          {!hasResults && !loading && query && (
            <div className="px-4 py-6 text-center text-slate-400 text-sm">
              No results found for <span className="text-slate-200">"{query}"</span>
            </div>
          )}

          {/* App context results */}
          {(results?.app_results.length ?? 0) > 0 && (
            <div>
              <div className="px-4 pt-3 pb-1 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                In App
              </div>
              {results!.app_results.map((item, i) => {
                const globalIdx = i;
                return (
                  <button
                    key={`app-${i}`}
                    onClick={() => activateItem({ type: 'app', item })}
                    onMouseEnter={() => setActiveIndex(globalIdx)}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                      activeIndex === globalIdx ? 'bg-slate-700' : 'hover:bg-slate-700/60'
                    }`}
                  >
                    <span className="flex-shrink-0 w-4 flex justify-center">
                      {CATEGORY_ICON[item.category]}
                    </span>
                    <div className="flex-1 min-w-0">
                      <span className="text-sm text-slate-100 font-medium">
                        {item.symbol ?? item.name}
                      </span>
                      {item.description && (
                        <span className="text-xs text-slate-400 ml-2 truncate">{item.description}</span>
                      )}
                    </div>
                    <span className="text-xs text-slate-500 flex-shrink-0">
                      {CATEGORY_LABEL[item.category]}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Market results */}
          {(results?.market_results.length ?? 0) > 0 && (
            <div>
              <div className={`px-4 pt-3 pb-1 text-xs font-semibold text-slate-500 uppercase tracking-wider ${
                (results?.app_results.length ?? 0) > 0 ? 'border-t border-slate-700' : ''
              }`}>
                Markets
              </div>
              {results!.market_results.map((item, i) => {
                const globalIdx = (results?.app_results.length ?? 0) + i;
                return (
                  <button
                    key={`market-${i}`}
                    onClick={() => activateItem({ type: 'market', item })}
                    onMouseEnter={() => setActiveIndex(globalIdx)}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                      activeIndex === globalIdx ? 'bg-slate-700' : 'hover:bg-slate-700/60'
                    }`}
                  >
                    <span className="flex-shrink-0 w-4 flex justify-center">
                      <TrendingUp size={13} className="text-slate-400" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <span className="text-sm text-slate-100 font-medium">{item.code}</span>
                      <span className="text-xs text-slate-400 ml-2 truncate">{item.name}</span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {item.exchange && (
                        <span className="text-xs bg-slate-700 text-slate-300 px-1.5 py-0.5 rounded">
                          {item.exchange}
                        </span>
                      )}
                      {item.type && (
                        <span className="text-xs text-slate-500">{item.type}</span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {/* Web results */}
          {(results?.web_results.length ?? 0) > 0 && (
            <div>
              <div className={`px-4 pt-3 pb-1 text-xs font-semibold text-slate-500 uppercase tracking-wider ${
                ((results?.app_results.length ?? 0) + (results?.market_results.length ?? 0)) > 0
                  ? 'border-t border-slate-700'
                  : ''
              }`}>
                Web — where to find this asset
              </div>
              {results!.web_results.map((item, i) => {
                const globalIdx = (results?.app_results.length ?? 0)
                  + (results?.market_results.length ?? 0) + i;
                return (
                  <button
                    key={`web-${i}`}
                    onClick={() => activateItem({ type: 'web', item })}
                    onMouseEnter={() => setActiveIndex(globalIdx)}
                    className={`w-full flex items-start gap-3 px-4 py-2.5 text-left transition-colors ${
                      activeIndex === globalIdx ? 'bg-slate-700' : 'hover:bg-slate-700/60'
                    }`}
                  >
                    <Globe size={13} className="text-slate-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-slate-100 font-medium truncate">{item.title}</div>
                      {item.snippet && (
                        <div className="text-xs text-slate-400 line-clamp-2 mt-0.5">{item.snippet}</div>
                      )}
                      <div className="text-xs text-slate-500 truncate mt-0.5">{item.url}</div>
                    </div>
                    <ArrowUpRight size={12} className="text-slate-500 flex-shrink-0 mt-0.5" />
                  </button>
                );
              })}
            </div>
          )}

          {hasResults && (
            <div className="px-4 py-2 border-t border-slate-700 text-xs text-slate-500">
              {results!.app_results.length} in-app · {results!.market_results.length} markets
              {results!.web_results.length > 0 && ` · ${results!.web_results.length} web`}
              &nbsp;·&nbsp;↑↓ navigate · Enter select · Esc close
            </div>
          )}
        </div>
      )}
    </div>
  );
}
